/**
 * codeWriteCore - Shared logic for code commands
 * Manages code files stored in Y.js (following note_write pattern)
 */

import * as Y from 'yjs';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { getApplicationForEntity } from '../../../services/applicationRegistry';

/**
 * Validate entity is a code project type
 */
export function validateCodeProject(entityId: string): string | null {
  const validPrefixes = ['app_proj_', 'app_project_', 'website_proj_', 'website_project_', 'code_proj_', 'code_project_'];
  const isValid = validPrefixes.some(prefix => entityId.startsWith(prefix));
  
  if (!isValid) {
    return `Invalid entity ID. Must be a code project (app_project, website_project, or code_project). Got: ${entityId}`;
  }
  
  return null;
}

/**
 * Get or create application instance for code project
 */
export async function getCodeProjectApp(entityId: string): Promise<any> {
  const app = await getApplicationForEntity(entityId);
  
  if (!app) {
    throw new Error(`Could not load application for ${entityId}`);
  }
  
  // Ensure filesMap exists in Y.doc (access via public property if available)
  const ydoc = (app as any).ydocPublic || (app as any).ydoc;
  if (!ydoc) {
    throw new Error('Application does not have Y.doc');
  }
  
  const filesMap = ydoc.getMap('files');
  if (!filesMap) {
    throw new Error('Application does not have files map');
  }
  
  return app;
}

/**
 * Execute write operation on a file
 */
export async function executeFileWrite(
  app: any,
  path: string,
  content: string
): Promise<{ path: string; operation: string; lines: number }> {
  const ydoc = app.ydocPublic || app.ydoc;
  const filesMap = ydoc.getMap('files') as Y.Map<Y.Text>;
  
  // Get or create Y.Text for file
  let ytext = filesMap.get(path) as Y.Text | undefined;
  if (!ytext) {
    ytext = new Y.Text();
    filesMap.set(path, ytext);
  }
  
  // Replace content using Y.js transaction
  ydoc.transact(() => {
    if (ytext) {
      ytext.delete(0, ytext.length);
      ytext.insert(0, content);
    }
  });
  
  return {
    path,
    operation: 'write',
    lines: content.split('\n').length
  };
}

/**
 * Execute update operation on a file (search/replace)
 */
export async function executeFileUpdate(
  app: any,
  path: string,
  search: string,
  replace: string
): Promise<{ path: string; operation: string; replacements: number }> {
  const ydoc = app.ydocPublic || app.ydoc;
  const filesMap = ydoc.getMap('files') as Y.Map<Y.Text>;
  const ytext = filesMap.get(path) as Y.Text | undefined;
  
  if (!ytext) {
    throw new Error(`File not found: ${path}`);
  }
  
  const currentContent = ytext.toString();
  
  // Count occurrences
  const regex = new RegExp(search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
  const matches = currentContent.match(regex);
  const replacementCount = matches ? matches.length : 0;
  
  if (replacementCount === 0) {
    throw new Error(`Search text not found in ${path}`);
  }
  
  // Replace all occurrences
  const newContent = currentContent.replace(regex, replace);
  
  // Update using Y.js transaction
  ydoc.transact(() => {
    ytext.delete(0, ytext.length);
    ytext.insert(0, newContent);
  });
  
  return {
    path,
    operation: 'update',
    replacements: replacementCount
  };
}

/**
 * Execute delete operation on a file
 */
export async function executeFileDelete(
  app: any,
  path: string
): Promise<{ path: string; operation: string }> {
  const ydoc = app.ydocPublic || app.ydoc;
  const filesMap = ydoc.getMap('files') as Y.Map<Y.Text>;
  
  if (!filesMap.has(path)) {
    throw new Error(`File not found: ${path}`);
  }
  
  filesMap.delete(path);
  
  return {
    path,
    operation: 'delete'
  };
}

/**
 * Execute rename operation on a file
 */
export async function executeFileRename(
  app: any,
  oldPath: string,
  newPath: string
): Promise<{ oldPath: string; newPath: string; operation: string }> {
  const ydoc = app.ydocPublic || app.ydoc;
  const filesMap = ydoc.getMap('files') as Y.Map<Y.Text>;
  
  const ytext = filesMap.get(oldPath) as Y.Text | undefined;
  if (!ytext) {
    throw new Error(`File not found: ${oldPath}`);
  }
  
  if (filesMap.has(newPath)) {
    throw new Error(`Target file already exists: ${newPath}`);
  }
  
  // Move file
  filesMap.set(newPath, ytext);
  filesMap.delete(oldPath);
  
  return {
    oldPath,
    newPath,
    operation: 'rename'
  };
}

/**
 * Execute batch operations
 */
export async function executeBatchOperations(
  app: any,
  operations: any[]
): Promise<{ operations: any[]; count: number }> {
  const results: any[] = [];
  const ydoc = app.ydocPublic || app.ydoc;
  
  // Execute all operations in a single transaction
  ydoc.transact(() => {
    for (const op of operations) {
      try {
        let result;
        switch (op.op || op.operation) {
          case 'write':
            result = executeFileWriteSync(app, op.path, op.content);
            break;
          case 'update':
            result = executeFileUpdateSync(app, op.path, op.search, op.replace);
            break;
          case 'delete':
            result = executeFileDeleteSync(app, op.path);
            break;
          case 'rename':
            result = executeFileRenameSync(app, op.oldPath, op.newPath);
            break;
          default:
            throw new Error(`Unknown operation: ${op.op || op.operation}`);
        }
        results.push({ success: true, ...result });
      } catch (error: any) {
        results.push({ success: false, error: error.message, op });
      }
    }
  });
  
  return {
    operations: results,
    count: results.filter(r => r.success).length
  };
}

// Synchronous versions for use within transactions
function executeFileWriteSync(app: any, path: string, content: string) {
  const ydoc = app.ydocPublic || app.ydoc;
  const filesMap = ydoc.getMap('files') as Y.Map<Y.Text>;
  let ytext = filesMap.get(path) as Y.Text | undefined;
  if (!ytext) {
    ytext = new Y.Text();
    filesMap.set(path, ytext);
  }
  ytext.delete(0, ytext.length);
  ytext.insert(0, content);
  return { path, operation: 'write', lines: content.split('\n').length };
}

function executeFileUpdateSync(app: any, path: string, search: string, replace: string) {
  const ydoc = app.ydocPublic || app.ydoc;
  const filesMap = ydoc.getMap('files') as Y.Map<Y.Text>;
  const ytext = filesMap.get(path) as Y.Text | undefined;
  if (!ytext) throw new Error(`File not found: ${path}`);
  
  const currentContent = ytext.toString();
  const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const newContent = currentContent.replace(new RegExp(escaped, 'g'), replace);
  
  ytext.delete(0, ytext.length);
  ytext.insert(0, newContent);
  
  return { path, operation: 'update' };
}

function executeFileDeleteSync(app: any, path: string) {
  const ydoc = app.ydocPublic || app.ydoc;
  const filesMap = ydoc.getMap('files') as Y.Map<Y.Text>;
  filesMap.delete(path);
  return { path, operation: 'delete' };
}

function executeFileRenameSync(app: any, oldPath: string, newPath: string) {
  const ydoc = app.ydocPublic || app.ydoc;
  const filesMap = ydoc.getMap('files') as Y.Map<Y.Text>;
  const ytext = filesMap.get(oldPath) as Y.Text | undefined;
  if (!ytext) throw new Error(`File not found: ${oldPath}`);
  if (filesMap.has(newPath)) throw new Error(`Target file already exists: ${newPath}`);
  
  filesMap.set(newPath, ytext);
  filesMap.delete(oldPath);
  
  return { oldPath, newPath, operation: 'rename' };
}

/**
 * Read file content
 */
export function readFile(app: any, path: string): string {
  const ydoc = app.ydocPublic || app.ydoc;
  const filesMap = ydoc.getMap('files') as Y.Map<Y.Text>;
  const ytext = filesMap.get(path) as Y.Text | undefined;
  
  if (!ytext) {
    throw new Error(`File not found: ${path}`);
  }
  
  return ytext.toString();
}

/**
 * List all files
 */
export function listFiles(app: any): string[] {
  const ydoc = app.ydocPublic || app.ydoc;
  const filesMap = ydoc.getMap('files') as Y.Map<Y.Text>;
  return Array.from(filesMap.keys());
}

/**
 * Search in files
 */
export function searchInFiles(app: any, query: string, files?: string[]): Array<{ path: string; matches: number }> {
  const ydoc = app.ydocPublic || app.ydoc;
  const filesMap = ydoc.getMap('files') as Y.Map<Y.Text>;
  const results: Array<{ path: string; matches: number }> = [];
  
  const filesToSearch = files || Array.from(filesMap.keys());
  const regex = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
  
  for (const path of filesToSearch) {
    const ytext = filesMap.get(path) as Y.Text | undefined;
    if (ytext) {
      const content = ytext.toString();
      const matches = content.match(regex);
      if (matches && matches.length > 0) {
        results.push({ path, matches: matches.length });
      }
    }
  }
  
  return results;
}

/**
 * Read the current error state from a code project application instance.
 * Populated by AppBuilderApp.tsx when compilation fails or runtime errors occur.
 */
export function getCodeProjectErrors(app: any): {
  compilationError: string | null;
  runtimeErrors: string[];
  hasErrors: boolean;
  status: 'ok' | 'compilation_error' | 'runtime_errors' | 'errors';
} {
  const compilationError: string | null = app.lastCompilationError ?? null;
  const runtimeErrors: string[] = app.recentRuntimeErrors ?? [];
  const hasErrors = compilationError !== null || runtimeErrors.length > 0;

  let status: 'ok' | 'compilation_error' | 'runtime_errors' | 'errors';
  if (!hasErrors) {
    status = 'ok';
  } else if (compilationError && runtimeErrors.length > 0) {
    status = 'errors';
  } else if (compilationError) {
    status = 'compilation_error';
  } else {
    status = 'runtime_errors';
  }

  return { compilationError, runtimeErrors, hasErrors, status };
}

/**
 * Build file tree structure
 */
export function buildFileTree(files: string[]): any {
  const tree: any = {};
  
  for (const path of files) {
    const parts = path.split('/').filter(Boolean);
    let current = tree;
    
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isFile = i === parts.length - 1;
      
      if (!current[part]) {
        current[part] = isFile ? null : {};
      }
      
      if (!isFile) {
        current = current[part];
      }
    }
  }
  
  return tree;
}
